import type { ApolloError } from '@apollo/client';
import {
  type Locale,
  siteNames,
  type SiteNamesMatchingClassification,
} from '@seek/melways-sites';
import type { FC, PropsWithChildren } from 'react';

import type { ConfigOptions } from 'src/modules/ConfigProvider';

export interface ClientContext {
  config: ConfigOptions;
  devContext?: DevContext;
}

export type EmployerSiteName = SiteNamesMatchingClassification<{
  product: 'employer';
}>;

export enum Environment {
  development = 'development',
  staging = 'staging',
  production = 'production',
}

export type TealiumEnvironment = 'dev' | 'qa' | 'prod';

export enum AuthStatus {
  UNAUTHENTICATED = 'unauthenticated',
  AUTHENTICATED = 'authenticated',
  PENDING = 'pending',
}

export enum ModuleTheme {
  LIGHT = 'light',
  MEDIUM = 'medium',
  DARK = 'dark',
}

export enum WindowConfigItemKey {
  SITE = 'site', // remove this when isNZ is removed
  LOCALE = 'locale',
  ENV = 'environment',
  IS_TRACKING_ENABLED = 'isTrackingEnabled',
  DEV_CONTEXT = 'devContext',
}

export interface WindowConfig {
  [WindowConfigItemKey.SITE]: EmployerSiteName;
  [WindowConfigItemKey.LOCALE]: Locale;
  [WindowConfigItemKey.IS_TRACKING_ENABLED]: boolean;
  [WindowConfigItemKey.ENV]: Environment;
  [WindowConfigItemKey.DEV_CONTEXT]?: DevContext;
}
export interface DevContext {
  advertiserId: string;
  seekAdvertiserId: string;
  authToken: string;
  email: string;
  useLocalGraph: boolean;
}

export enum Permission {
  CanCreatePosition = 'canCreatePosition',
  HasAllPositionAccess = 'hasAllPositionAccess',
  HasSpecificPositionAccess = 'hasSpecificPositionAccess',
  HasPremiumTalentSearch = 'hasPremiumTalentSearch',
  HasAnalytics = 'hasAnalytics',
}

export const PermissionDisplayNames: Record<Permission, string> = {
  canCreatePosition: 'Job posting',
  hasAllPositionAccess: 'All jobs access',
  hasSpecificPositionAccess: 'Specific jobs access',
  hasPremiumTalentSearch: 'Premium Talent Search',
  hasAnalytics: 'Manager reports',
};

export interface UseQueryResponse<T> {
  loading: boolean;
  error?: ApolloError;
  data: T | null;
}

export type PermissionCategory = 'General' | 'Analytics';

export interface UserPermissionDetails {
  title: string;
  description: string;
}

export interface AccountInfoError extends Error {
  status?: number;
}

export interface PageData {
  eventName: string;
  brand: 'seek' | 'jobsdb' | 'jobstreet' | 'jora' | undefined;
  siteCountry:
    | 'au'
    | 'nz'
    | 'hk'
    | 'id'
    | 'my'
    | 'ph'
    | 'sg'
    | 'th'
    | 'vn'
    | undefined;
  zone: string;
  siteSection: string;
  siteSubsection: string;
  siteLanguage: string;
  currentPage: string;
  seekAdvertiserId: string;
  advertiserAdCentreId: string;
  userId: string;
  hirerUserEmail: string;
  hirerUserFullName: string;
  isLoggedIn: boolean;
  [key: string]: unknown;
}

export type FCC<P = Record<string, unknown>> = FC<PropsWithChildren<P>>;

export const isValidType = (config: {
  site: unknown;
  locale: unknown;
  environment: unknown;
}): config is Pick<ConfigOptions, 'site' | 'locale' | 'environment'> =>
  isEmployerSiteName(config.site) &&
  isLocale(config.locale) &&
  isEnvironment(config.environment);

const isEnvironment = (environment: unknown): environment is Environment =>
  environment === 'development' ||
  environment === 'staging' ||
  environment === 'production';

const isEmployerSiteName = (site: unknown): site is EmployerSiteName =>
  // @ts-expect-error
  Object.values(siteNames.employer.seek).includes(site);

const locales: Locale[] = [
  'en-AU',
  'en-NZ',
  'en-HK',
  'en-TH',
  'en-SG',
  'en-ID',
  'en-PH',
  'en-MY',
  'th-TH',
  'id-ID',
];
const isLocale = (locale: unknown): locale is Locale =>
  // @ts-expect-error
  locales.includes(locale);
